import { addDays, startOfDay } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { compareEventsByStart } from '../../eventOrder'
import { clipToDay } from '../../eventSpan'

// Geometry measured from the prototype (LAYOUT-ARCHITECTURE.md §4.1). The body
// spans the whole day; `TimeScroller` shows a window of it.
export const START_HOUR = 0
export const END_HOUR = 24
export const HOUR_PX = 56
const MIN_TILE_PX = 20
// A little room past midnight, so a tile starting late keeps its real start
// and still fits at the minimum height, instead of being shifted up.
export const BODY_HEIGHT = (END_HOUR - START_HOUR) * HOUR_PX + MIN_TILE_PX
export const GUTTER = '52px'
export const GRID_COLUMNS = `${GUTTER} repeat(7, minmax(0, 1fr))`

export const HOURS = Array.from(
  { length: END_HOUR - START_HOUR },
  (_, i) => START_HOUR + i,
)

const MIN_DURATION_HOURS = 0.5
const HOUR_MS = 3_600_000

/**
 * `date`'s position on `day`'s wall-clock grid, in hours: 0 at or before the
 * day's start, 24 at or after its end. Read from the clock (getHours), never
 * from elapsed time since midnight — on a DST transition day 9:00 is 8 or 10
 * elapsed hours after midnight, which would shift tiles off the hour gridlines.
 */
function wallHour(date: Date, day: Date): number {
  if (date <= day) return 0
  if (date >= addDays(day, 1)) return 24
  return date.getHours() + date.getMinutes() / 60
}

/**
 * Vertical offset (px) of a wall-clock hour within the body. The single formula
 * shared by tiles and overlays, so they can never drift apart.
 */
function topForHour(hour: number): number {
  return Math.round((hour - START_HOUR) * HOUR_PX) + 1
}

export type TimedGeometry = { top: number; height: number }

/**
 * Absolute-position geometry for a timed tile within `day`'s body
 * (SPEC-EventTile.md §2). The body covers the whole day, so a tile sits at its
 * real start and end; one that runs past midnight is cut at the day's end.
 */
export function timedGeometry(
  start: Date,
  end: Date,
  day: Date,
): TimedGeometry {
  const startH = wallHour(start, day)
  const endH = Math.max(wallHour(end, day), startH + MIN_DURATION_HOURS)

  const top = topForHour(startH)
  const height = Math.max(
    MIN_TILE_PX,
    Math.round((Math.min(endH, END_HOUR) - startH) * HOUR_PX) - 2,
  )
  return { top, height: Math.min(height, BODY_HEIGHT - top) }
}

/**
 * Vertical offset (px) of a wall-clock instant, matching a tile starting then.
 * Unbounded: the room past midnight in `BODY_HEIGHT` keeps 23:59 inside.
 */
export function topForTime(date: Date): number {
  return topForHour(wallHour(date, startOfDay(date)))
}

/** Snap granularity for create-on-grid: clicks resolve to a half-hour slot. */
export const SLOT_MINUTES = 30
export const SLOT_PX = HOUR_PX / (60 / SLOT_MINUTES)
/**
 * Last slot a click may resolve to. Slot 0 starts at START_HOUR, so the final
 * one starts a half hour before END_HOUR — a start *at* END_HOUR is off-grid.
 */
export const SLOT_MAX = (END_HOUR - START_HOUR) * (60 / SLOT_MINUTES) - 1

/**
 * The slot containing a pointer `offsetY` px below the body's top edge.
 *
 * Measured against the hour gridlines, so it is deliberately *not* the inverse
 * of `topForHour` — that adds a 1px inset to keep a tile clear of its gridline,
 * and subtracting it here would push every click on a boundary into the
 * previous slot.
 *
 * Floors rather than rounds: the AC is "the slot I clicked into is the slot I
 * get" (09:00–09:29 → 09:00). The design handoff's v4 `Math.round` was
 * considered and overridden by the team, so the top edge is inclusive and the
 * bottom exclusive.
 *
 * The clamp is load-bearing at both ends: `offsetY === BODY_HEIGHT` is
 * reachable through sub-pixel/devicePixelRatio rounding and would otherwise
 * yield a start at END_HOUR, and a negative offset (transform or scroll bug)
 * would resolve before the grid begins.
 */
export function slotFromOffsetY(offsetY: number): number {
  const raw = Math.floor(offsetY / SLOT_PX)
  return Math.min(Math.max(raw, 0), SLOT_MAX)
}

/**
 * Wall-clock start time for a click `offsetY` px into `day`'s timed body.
 *
 * Builds the time with `setHours` on the day's midnight rather than adding
 * elapsed milliseconds: on a DST transition day the two disagree by the offset,
 * which would slide every slot after the transition. Same reason `wallHour`
 * reads `getHours()`.
 */
export function startFromOffsetY(day: Date, offsetY: number): Date {
  const total = START_HOUR * 60 + slotFromOffsetY(offsetY) * SLOT_MINUTES
  const start = startOfDay(day)
  start.setHours(Math.floor(total / 60), total % 60, 0, 0)
  return start
}

const EDGE_PX = 2

export type PositionedTimedEvent = {
  event: CalendarEvent
  top: number
  height: number
  /** CSS `left` — a px edge inset for a lone event, or a `%` for a lane. */
  left: number | string
  /** CSS `width` — omitted (undefined) for a lone event (which uses `right`). */
  width?: string
  /** CSS `right` — the px edge inset when an event owns the full column width. */
  right?: number
}

/** A "+N" marker standing in for events that did not fit the lane cap. */
export type MoreMarker = {
  count: number
  top: number
  left: string
  width: string
}

export type TimedLayout = {
  tiles: PositionedTimedEvent[]
  more: MoreMarker[]
}

/**
 * Lay `day`'s timed events out into non-overlapping horizontal lanes so
 * intersecting appointments sit side by side instead of painting on top of one
 * another. Multi-day events are clipped to their slice of `day`.
 *
 * Events are grouped into clusters of transitively-overlapping events; within a
 * cluster each event takes the first free lane and every event shares the cluster
 * lane count, so widths line up. A cluster of one keeps the full-width px insets.
 * Past `maxLanes`, lanes would become unreadable slivers, so the last lane
 * becomes a "+N" marker for the events that did not fit.
 */
export function layoutTimedEvents(
  events: CalendarEvent[],
  day: Date,
  maxLanes = Number.POSITIVE_INFINITY,
): TimedLayout {
  const spans = [...events].sort(compareEventsByStart).map((event) => {
    const { start, end } = clipToDay(event, day)
    const startMs = start.getTime()
    // Lanes follow the drawn extent: short events render at the minimum
    // duration, so two back-to-back 5-minute events still overlap visually.
    const endMs = Math.max(
      end.getTime(),
      startMs + MIN_DURATION_HOURS * HOUR_MS,
    )
    return { event, start, end, startMs, endMs }
  })

  const tiles: PositionedTimedEvent[] = []
  const more: MoreMarker[] = []
  let cluster: typeof spans = []
  let clusterMaxEnd = -Infinity

  const flush = () => {
    if (cluster.length === 0) return
    // Greedy lane assignment: each event takes the first lane whose last event
    // has already ended.
    const laneEnds: number[] = []
    const laneOf = new Map<(typeof cluster)[number], number>()
    for (const span of cluster) {
      let lane = laneEnds.findIndex((end) => end <= span.startMs)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(span.endMs)
      } else {
        laneEnds[lane] = span.endMs
      }
      laneOf.set(span, lane)
    }

    const lanes = Math.min(laneEnds.length, maxLanes)
    const overflowing = laneEnds.length > maxLanes
    // With overflow, the last visible lane is given to the marker.
    const tileLanes = overflowing ? lanes - 1 : lanes
    const widthPct = 100 / lanes
    const hidden: typeof cluster = []

    for (const span of cluster) {
      const lane = laneOf.get(span) as number
      if (lane >= tileLanes) {
        hidden.push(span)
        continue
      }
      const { top, height } = timedGeometry(span.start, span.end, day)
      if (lanes === 1) {
        tiles.push({
          event: span.event,
          top,
          height,
          left: EDGE_PX,
          right: EDGE_PX,
        })
      } else {
        tiles.push({
          event: span.event,
          top,
          height,
          left: `${lane * widthPct}%`,
          width: `calc(${widthPct}% - ${EDGE_PX}px)`,
        })
      }
    }

    if (hidden.length > 0) {
      more.push({
        count: hidden.length,
        top: timedGeometry(hidden[0].start, hidden[0].end, day).top,
        left: `${tileLanes * widthPct}%`,
        width: `calc(${widthPct}% - ${EDGE_PX}px)`,
      })
    }

    cluster = []
    clusterMaxEnd = -Infinity
  }

  for (const span of spans) {
    if (cluster.length > 0 && span.startMs >= clusterMaxEnd) flush()
    cluster.push(span)
    clusterMaxEnd = Math.max(clusterMaxEnd, span.endMs)
  }
  flush()

  return { tiles, more }
}
